import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Card, CardContent, CardHeader, Typography, Button, Chip, Table, TableHead,
  TableRow, TableCell, TableBody, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Avatar, Divider, Paper,
  CircularProgress,
} from '@mui/material';
import {
  EventAvailable as QueueIcon,
  Add as AddIcon,
  PlayCircleOutline as StartIcon,
  CheckCircleOutline as DoneIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  RemoveCircleOutline as SkipIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { useStartSession } from '../components/StartSessionGate';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const POLL_MS = 30_000;

const STATUS_META = {
  waiting: { label: 'Waiting', color: 'info' },
  in_progress: { label: 'In progress', color: 'warning' },
  done: { label: 'Done', color: 'success' },
  skipped: { label: 'Skipped', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export default function QueuePage() {
  const { user } = useAuth();
  const { startSession, gateDialog } = useStartSession();
  const canMutate = user?.role === 'clinic_admin' || user?.role === 'staff';
  // Providers and clinic admins can scribe — they can jump straight from the
  // queue into a recording session for a waiting / in-progress patient.
  const canScribe = user?.role === 'provider' || user?.role === 'clinic_admin';
  const [data, setData] = useState({ entries: [], counts: {}, currently_serving_position: null, providers: [] });
  const [loading, setLoading] = useState(true);

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [form, setForm] = useState({ patient_id: '', provider_id: '', reason: '' });
  const [adding, setAdding] = useState(false);

  // The row currently being acted on, so its controls can show they are working.
  const [busyId, setBusyId] = useState(null);
  // The clinician whose next patient is being called, so the button can show it.
  const [callingId, setCallingId] = useState(null);

  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/queue/today`);
      setData(res.data);
    } catch (err) {
      // surface only on first load
      if (loading) toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAddDialogOptions = async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API_URL}/api/v1/admin/patients`),
        axios.get(`${API_URL}/api/v1/admin/assignable-clinicians`),
      ]);
      setPatients((pRes.data?.patients || []).filter((p) => p.is_active !== false));
      setClinicians(cRes.data?.clinicians || []);
    } catch {
      toast.error('Failed to load options');
    }
  };

  const openAdd = () => {
    setForm({ patient_id: '', provider_id: '', reason: '' });
    loadAddDialogOptions();
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!form.patient_id) return;
    setAdding(true);
    try {
      await axios.post(`${API_URL}/api/v1/queue`, {
        patient_id: form.patient_id,
        provider_id: form.provider_id || null,
        reason: form.reason || null,
      });
      toast.success('Patient added to queue');
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add to queue');
    } finally {
      setAdding(false);
    }
  };

  const updateStatus = async (entry, status) => {
    // Without this the row gives no sign anything happened. That was survivable when
    // the call was instant and misleading when it was not: people clicked, saw
    // nothing, and reloaded the page to find out whether it had worked.
    setBusyId(entry.id);
    try {
      await axios.put(`${API_URL}/api/v1/queue/${entry.id}/status`, { status });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const callNext = async (p) => {
    setCallingId(p.provider_id);
    try {
      const { data: st } = await axios.post(`${API_URL}/api/v1/queue/call-next`,
        { provider_id: p.provider_id });
      toast.success(st?.current?.token
        ? `Called ${st.current.token} — ${st.current.patient_name}`
        : 'Called the next patient');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not call the next patient');
    } finally {
      setCallingId(null);
    }
  };

  const move = async (entry, direction) => {
    setBusyId(entry.id);
    try {
      await axios.put(`${API_URL}/api/v1/queue/${entry.id}/move`, { direction });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder');
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (entry) => {
    if (!window.confirm(`Cancel ${entry.patient_name}'s queue entry?`)) return;
    try {
      await axios.delete(`${API_URL}/api/v1/queue/${entry.id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const { waiting, inProgress, finished } = useMemo(() => {
    const w = []; const ip = []; const f = [];
    (data.entries || []).forEach((e) => {
      if (e.status === 'waiting') w.push(e);
      else if (e.status === 'in_progress') ip.push(e);
      else f.push(e);
    });
    return { waiting: w, inProgress: ip, finished: f };
  }, [data]);

  const renderRow = (e, indexInGroup, totalInGroup, group) => (
    <TableRow key={e.id} hover>
      <TableCell sx={{ width: 64 }}>
        {/* The token is what the patient was actually told. An entry with no
            clinician has none — showing its clinic-wide position there read as a
            token and meant nothing to anyone. */}
        <Chip label={e.token || 'No token'}
          variant={e.token ? 'filled' : 'outlined'}
          color={e.token ? (STATUS_META[e.status]?.color || 'default') : 'default'}
          size="small" />
      </TableCell>
      <TableCell>
        <Typography fontWeight={600}>{e.patient_name || '—'}</Typography>
        {e.patient_phone && (
          <Typography variant="caption" color="text.secondary">{e.patient_phone}</Typography>
        )}
      </TableCell>
      <TableCell>{e.provider_name || <Chip size="small" label="Unassigned" color="warning" />}</TableCell>
      <TableCell>{e.reason || '—'}</TableCell>
      <TableCell>
        <Chip size="small" label={STATUS_META[e.status]?.label || e.status} color={STATUS_META[e.status]?.color || 'default'} variant="outlined" />
      </TableCell>
      <TableCell>
        {e.status === 'waiting' && e.checked_in_at && (
          <Typography variant="caption" color="text.secondary">
            Checked in {new Date(e.checked_in_at).toLocaleTimeString()}
          </Typography>
        )}
        {e.status === 'in_progress' && e.called_at && (
          <Typography variant="caption" color="text.secondary">
            Called {new Date(e.called_at).toLocaleTimeString()}
          </Typography>
        )}
        {(e.status === 'done' || e.status === 'skipped' || e.status === 'cancelled') && (
          <Typography variant="caption" color="text.secondary">
            {e.completed_at ? new Date(e.completed_at).toLocaleTimeString() : ''}
            {e.service_seconds ? ` · ${formatDuration(e.service_seconds)}` : ''}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        {canScribe && (group === 'waiting' || group === 'in_progress') && (
          <Tooltip title="Start recording session">
            <IconButton size="small" color="primary" onClick={() => startSession(e.patient_id)}>
              <MicIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canMutate && group === 'waiting' && (
          <>
            <Tooltip title="Move up">
              <span>
                <IconButton size="small" disabled={indexInGroup === 0} onClick={() => move(e, 'up')}>
                  <UpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton size="small" disabled={indexInGroup === totalInGroup - 1} onClick={() => move(e, 'down')}>
                  <DownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Skip">
              <IconButton size="small" onClick={() => updateStatus(e, 'skipped')}>
                <SkipIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton size="small" color="error" onClick={() => cancel(e)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {canMutate && group === 'in_progress' && (
          <Tooltip title="Mark done">
            <IconButton size="small" color="success" onClick={() => updateStatus(e, 'done')}>
              <DoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      {gateDialog}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><QueueIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Today's Queue</Typography>
            <Typography variant="body2" color="text.secondary">
              {data.counts?.waiting || 0} waiting · {data.counts?.in_progress || 0} in progress · {data.counts?.completed || 0} completed
            </Typography>
            {/* Per clinician, because a clinic-wide "now serving" number means nothing
                to a patient waiting for one particular doctor. */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
              {(data.providers || []).map((p) => (
                <Box key={p.provider_id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip
                    size="small"
                    variant={p.is_seeing_patients ? 'filled' : 'outlined'}
                    color={p.is_seeing_patients ? 'success' : 'default'}
                    label={`${p.provider_name || 'Unassigned'} — ${
                      p.now_serving_token ? `with ${p.now_serving_token}`
                        : (p.is_with_patient ? `with ${p.now_serving_patient_name || 'a patient'}` : 'nobody in')
                    }${p.on_break ? ' · on a break' : (p.is_seeing_patients ? '' : ' · not in yet')} · ${p.waiting} waiting`}
                  />
                  {/* The only way a patient is called forward: the front of THIS
                      clinician's line. Never a row picked out of the middle. */}
                  {/* Not while they are on a break: calling someone in would tell the
                      patient it is their turn with nobody in the room. */}
                  {canMutate && p.waiting > 0 && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={callingId === p.provider_id || p.on_break}
                      title={p.on_break ? 'On a break — they call their next patient when back' : undefined}
                      startIcon={callingId === p.provider_id
                        ? <CircularProgress size={13} />
                        : <StartIcon fontSize="small" />}
                      onClick={() => callNext(p)}
                    >
                      Call next
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          {canMutate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add to queue
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Card><CardContent><Typography>Loading…</Typography></CardContent></Card>
      ) : (
        <>
          {/* Currently serving */}
          <Card sx={{ mb: 3, bgcolor: 'warning.50' }}>
            <CardHeader title="Currently being seen" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <CardContent sx={{ pt: 0 }}>
              {inProgress.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No patient is currently being seen.</Typography>
              ) : (
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Token</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Doctor</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inProgress.map((e, i) => renderRow(e, i, inProgress.length, 'in_progress'))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Waiting */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Waiting" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }} />
            <CardContent sx={{ pt: 0 }}>
              {waiting.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No one is waiting.</Typography>
              ) : (
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Token</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Doctor</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {waiting.map((e, i) => renderRow(e, i, waiting.length, 'waiting'))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Finished */}
          {finished.length > 0 && (
            <Card>
              <CardHeader
                title="Finished today"
                titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700, color: 'text.secondary' }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Token</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Doctor</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Time / duration</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {finished.map((e, i) => renderRow(e, i, finished.length, 'done'))}
                    </TableBody>
                  </Table>
                </Paper>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add patient to queue</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth select label="Patient" margin="normal" required
            value={form.patient_id}
            onChange={(e) => {
              const pid = e.target.value;
              const p = patients.find((x) => x.id === pid);
              setForm((f) => ({
                ...f,
                patient_id: pid,
                provider_id: p?.provider_id || f.provider_id,
                reason: p?.reason || f.reason,
              }));
            }}
          >
            {patients.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.full_name} {p.email ? `· ${p.email}` : ''}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth select label="Doctor" margin="normal"
            helperText="Defaults to the patient's assigned doctor"
            value={form.provider_id}
            onChange={(e) => setForm((f) => ({ ...f, provider_id: e.target.value }))}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {clinicians.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.full_name}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth label="Reason / Chief complaint" margin="normal"
            value={form.reason}
            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
          />
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            The patient will get a portal notification with their token number. The assigned doctor will be notified too.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={!form.patient_id || adding}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
